/**
 * Pluggable storage adapters for auth token persistence.
 *
 * The `StorageAdapter` interface mirrors a subset of the Web Storage API.
 * Three default implementations are provided for common use cases.
 * All implementations apply a configurable namespace prefix to keys.
 */

import type { StorageAdapter } from "./types";

/** Known auth storage keys (pre-namespace). Shared by manager and adapter. */
export const STORAGE_KEYS = {
  sessionSnapshot: "session_snapshot",
  refreshLease: "refresh_lease",
} as const;

/**
 * Pick a default storage adapter for the current environment.
 *
 * Returns an in-memory adapter in every environment. Applications that accept
 * the XSS exposure of persistent browser token storage must opt in explicitly.
 */
export function createDefaultStorageAdapter(): StorageAdapter {
  return createMemoryAdapter();
}

/** Creates a namespaced wrapper around a `StorageAdapter`. */
export function createNamespacedAdapter(inner: StorageAdapter, namespace: string): StorageAdapter {
  const prefix = `zero_${namespace}_`;
  return {
    sharing: inner.sharing,
    getItem(key: string): string | null {
      return inner.getItem(prefix + key);
    },
    setItem(key: string, value: string): void {
      inner.setItem(prefix + key, value);
    },
    removeItem(key: string): void {
      inner.removeItem(prefix + key);
    },
    /** Removes only known auth keys under this namespace. Does NOT clear the entire store. */
    clear(): void {
      for (const key of Object.values(STORAGE_KEYS)) {
        inner.removeItem(prefix + key);
      }
    },
  };
}

/**
 * Default adapter using `localStorage`.
 *
 * Persists across browser sessions and tabs.
 * This adapter is never selected implicitly because refresh tokens in
 * JavaScript-readable persistent storage turn XSS into long-lived takeover.
 */
export function createLocalStorageAdapter(): StorageAdapter {
  return {
    sharing: "same-origin",
    getItem(key: string): string | null {
      return localStorage.getItem(key);
    },
    setItem(key: string, value: string): void {
      localStorage.setItem(key, value);
    },
    removeItem(key: string): void {
      localStorage.removeItem(key);
    },
    clear(): void {
      localStorage.clear();
    },
  };
}

/**
 * Adapter using `sessionStorage`.
 *
 * Data is scoped to a single tab and cleared when the tab closes.
 */
export function createSessionStorageAdapter(): StorageAdapter {
  return {
    sharing: "single-context",
    getItem(key: string): string | null {
      return sessionStorage.getItem(key);
    },
    setItem(key: string, value: string): void {
      sessionStorage.setItem(key, value);
    },
    removeItem(key: string): void {
      sessionStorage.removeItem(key);
    },
    clear(): void {
      sessionStorage.clear();
    },
  };
}

/**
 * In-memory adapter using a `Map`.
 *
 * Suitable for SSR, testing, or environments without browser storage.
 * Data is lost when the process exits.
 */
export function createMemoryAdapter(): StorageAdapter {
  const store = new Map<string, string>();
  return {
    sharing: "single-context",
    getItem(key: string): string | null {
      return store.get(key) ?? null;
    },
    setItem(key: string, value: string): void {
      store.set(key, value);
    },
    removeItem(key: string): void {
      store.delete(key);
    },
    clear(): void {
      store.clear();
    },
  };
}
